import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { ArrowRight, Send, Users, Globe, Target, Handshake, CheckCircle } from 'lucide-react';

export default function AboutPage() {
  const { login } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', subject: 'General Feedback', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/feedback', form);
      setSent(true);
      setForm({ name: '', email: '', subject: 'General Feedback', message: '' });
    } catch (error) {
      alert('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="header-glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-neutral-900 rounded-sm flex items-center justify-center text-white font-bold text-sm">RE</div>
            <span className="font-bold text-xl" style={{ fontFamily: 'Outfit' }}>Research Exchange</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">Home</Link>
            <a href="#our-story" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">Our Story</a>
            <a href="#contact" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-20 px-6 sm:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold uppercase tracking-wider text-[#FF6B35] mb-4 block">About Us</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>
            Building a Fairer Market for<br />
            <span className="text-[#0D7377]">Research Talent</span>
          </h1>
          <p className="text-lg sm:text-xl text-neutral-600 max-w-2xl mx-auto leading-relaxed">
            The research industry runs on relationships. We believe it should also run on merit.
            Research Exchange exists to give every skilled researcher — freelancer, consultant, or agency —
            a fair shot at the best work, regardless of who they know.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section id="our-story" className="py-20 bg-neutral-50 px-6 sm:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-4 block">The Problem</span>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>
                Great talent gets overlooked.<br />Great projects miss the right people.
              </h2>
              <div className="space-y-4 text-neutral-600 leading-relaxed">
                <p>
                  In market research, work flows through personal networks. Clients rely on the same
                  handful of contacts. Freelancers struggle to find projects beyond word-of-mouth.
                  Agencies miss out on talent they've never met. The result? A fragmented industry
                  where the best match rarely happens.
                </p>
                <p>
                  For independent researchers and small agencies, this is especially acute. They do
                  exceptional work but lack the visibility that larger firms enjoy. Meanwhile, clients
                  settle for "good enough" because finding better is too hard.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {[
                { icon: Users, title: 'For Freelancers', desc: 'Stop waiting for referrals. Showcase your methods, sectors, and past work. Let the right projects find you.' },
                { icon: Handshake, title: 'For Agencies', desc: 'Expand your researcher pool beyond your rolodex. Discover specialists across geographies and methodologies.' },
                { icon: Target, title: 'For Clients', desc: 'Go beyond known references. Post a brief and let qualified researchers come to you — from across the country and the world.' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="bg-white p-5 border border-neutral-200">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 bg-[#FFF0EB]">
                        <Icon size={20} className="text-[#FF6B35]" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                        <p className="text-sm text-neutral-600">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* What We Stand For */}
      <section className="py-20 px-6 sm:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-wider text-[#0D7377] mb-4 block">What We Stand For</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Open Doors, Not Gatekeeping
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Merit Over Networks', desc: 'Skills, experience, and methodology should win work — not just knowing the right people.' },
              { title: 'Support the Independents', desc: 'Freelancers and small teams are the backbone of qualitative research. They deserve a platform that works for them.' },
              { title: 'Expand the Pool', desc: 'Help clients discover talent they would never have found through personal contacts alone — across cities and borders.' },
              { title: 'Transparency', desc: 'Clear briefs, visible rates, honest profiles. No hidden agendas, no middlemen taking a cut.' },
              { title: 'Quality First', desc: 'Verified profiles, detailed methodologies, and real credentials. Trust is earned, not assumed.' },
              { title: 'Global Reach, Local Depth', desc: 'Whether you need a focus group moderator in Mumbai or a semiotician in London — find them here.' },
            ].map((item, i) => (
              <div key={i} className="p-6 border border-neutral-200 hover:border-[#0D7377] transition-colors">
                <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Feedback */}
      <section id="contact" className="py-20 bg-neutral-900 text-white px-6 sm:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-[#FF6B35] mb-4 block">Get In Touch</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>
              We'd Love to Hear From You
            </h2>
            <p className="text-neutral-400">
              Whether it's a feature idea, a partnership opportunity, or just a thought about the industry —
              we're all ears. Every message reaches our founding team directly.
            </p>
          </div>

          {sent ? (
            <div className="text-center py-12" data-testid="feedback-success">
              <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
              <p className="text-neutral-400 mb-6">Thank you for reaching out. We'll get back to you soon.</p>
              <button onClick={() => setSent(false)} className="text-sm text-[#FF6B35] hover:underline">Send another message</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="feedback-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-1">Your Name</label>
                  <input
                    type="text"
                    data-testid="feedback-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Jane Doe"
                    className="w-full px-4 py-3 bg-[#1a1a2e] border border-[#2a2a4e] text-white placeholder:text-neutral-600 focus:border-[#FF6B35] outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-1">Your Email</label>
                  <input
                    type="email"
                    data-testid="feedback-email"
                    required
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="jane@example.com"
                    className="w-full px-4 py-3 bg-[#1a1a2e] border border-[#2a2a4e] text-white placeholder:text-neutral-600 focus:border-[#FF6B35] outline-none text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-1">Subject</label>
                <select
                  data-testid="feedback-subject"
                  value={form.subject}
                  onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                  className="w-full px-4 py-3 bg-[#1a1a2e] border border-[#2a2a4e] text-white focus:border-[#FF6B35] outline-none text-sm"
                >
                  <option>General Feedback</option>
                  <option>Feature Request</option>
                  <option>Partnership Enquiry</option>
                  <option>Bug Report</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-1">Message</label>
                <textarea
                  data-testid="feedback-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Tell us what's on your mind..."
                  className="w-full px-4 py-3 bg-[#1a1a2e] border border-[#2a2a4e] text-white placeholder:text-neutral-600 focus:border-[#FF6B35] outline-none text-sm resize-none"
                />
              </div>
              <button
                type="submit"
                data-testid="feedback-submit"
                disabled={sending}
                className="btn-researcher px-8 py-3 font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Send size={16} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 sm:px-12 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
            Ready to join the exchange?
          </h2>
          <p className="text-neutral-500 mb-8">Whether you're a researcher looking for work or a client looking for talent — there's a place for you here.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => login('researcher')} className="btn-researcher px-8 py-4 font-semibold text-lg flex items-center justify-center gap-2">
              I'm a Researcher <ArrowRight size={20} />
            </button>
            <button onClick={() => login('client')} className="btn-client px-8 py-4 font-semibold text-lg flex items-center justify-center gap-2">
              I'm a Client <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-neutral-900 rounded-sm flex items-center justify-center text-white font-bold text-xs">RE</div>
            <span className="font-semibold" style={{ fontFamily: 'Outfit' }}>Research Exchange</span>
          </div>
          <p className="text-sm text-neutral-500">2026 Research Exchange. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
